// «Marcar como reintegrado»: anota que un cobro duplicado ya se devolvió.
//
// Lo usan la fila del duplicado en Requiere atención y la tarjeta «Intentos de
// pago» de la ficha. No devuelve plata: deja asentado que alguien ya la
// devolvió (con la referencia que anota), y el duplicado sale de /cobros, del
// contador del menú y de la alerta del panel. Exige Usuarios en nivel completo
// y el permiso danger_zone (lib/access.ts → canMarkRefunded); el backend
// re-verifica todo, incluido que el pago sea un duplicado de ESE cliente.

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Undo2 } from 'lucide-react';
import { adminMarkAttemptRefunded, AdminApiError } from '@/lib/api';
import { money } from '@/lib/format';
import { REFUND_REFERENCE_MAX, refundReferenceProblem, shortPaymentId } from '@/lib/reconcile';
import { canMarkRefunded } from '@/lib/access';
import { useSession } from '@/hooks/useSession';
import ConfirmDialog from '@/components/ConfirmDialog';
import { fieldCls } from '@/lib/form-styles';

export default function MarkRefundedButton({
  userId,
  paymentId,
  amount,
  currency,
  customer,
}: {
  userId: string;
  paymentId: string;
  amount: string | number;
  currency: string;
  customer?: string | null;
}): JSX.Element {
  const session = useSession();
  const allowed = canMarkRefunded(session);
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reference, setReference] = useState('');
  const [touched, setTouched] = useState(false);

  const problem = refundReferenceProblem(reference);
  const mut = useMutation({
    mutationFn: () => adminMarkAttemptRefunded(userId, paymentId, reference.trim()),
    onSuccess: () => {
      setOpen(false);
      setReference('');
      setTouched(false);
      void qc.invalidateQueries({ queryKey: ['admin-user-detail'] });
      void qc.invalidateQueries({ queryKey: ['admin-pending-charges'] });
    },
  });

  return (
    <>
      <button
        type="button"
        data-help="charges.mark-refunded"
        onClick={() => {
          mut.reset();
          setOpen(true);
        }}
        disabled={!allowed}
        title={
          allowed
            ? undefined
            : 'Requiere Usuarios en nivel completo y el permiso danger_zone'
        }
        className="min-h-[2rem] px-2.5 text-xs inline-flex items-center gap-1.5 rounded-lg font-medium border border-status-error/40 text-status-error hover:bg-status-error/10 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-status-error"
      >
        <Undo2 size={14} aria-hidden="true" />
        Marcar como reintegrado
      </button>

      <ConfirmDialog
        open={open}
        title="Marcar como reintegrado"
        description={
          <div className="space-y-3">
            <p>
              Vas a dejar asentado que el pago{' '}
              <span className="font-mono text-ink dark:text-ink-inverted" title={paymentId}>
                {shortPaymentId(paymentId)}
              </span>{' '}
              de <strong className="text-ink dark:text-ink-inverted">{money(amount, currency)}</strong>
              {customer ? ` de ${customer}` : ''} <strong>ya se devolvió</strong>.
            </p>
            <ul className="list-disc pl-5 space-y-1 text-xs">
              <li>Esto no devuelve la plata: confirmá antes que el reintegro se hizo.</li>
              <li>Sale de Requiere atención y en la ficha queda como «Reintegrado».</li>
              <li>No se puede deshacer desde acá.</li>
            </ul>
            <label className="block text-xs">
              <span className="font-medium">
                Referencia del reintegro (n.º de operación o comentario)
              </span>
              <input
                type="text"
                value={reference}
                maxLength={REFUND_REFERENCE_MAX}
                onChange={(e) => setReference(e.target.value)}
                onBlur={() => setTouched(true)}
                placeholder="Ej.: operación 778899 de la pasarela"
                aria-invalid={touched && problem ? true : undefined}
                className={fieldCls({ extra: 'mt-1', invalid: Boolean(touched && problem) })}
              />
            </label>
            {touched && problem ? <p className="text-status-error text-xs">{problem}</p> : null}
            {mut.isError ? (
              <p className="text-status-error text-xs">
                Error: {mut.error instanceof AdminApiError ? mut.error.message : 'desconocido'}
              </p>
            ) : null}
          </div>
        }
        confirmLabel="Marcar como reintegrado"
        loading={mut.isPending}
        onConfirm={() => {
          setTouched(true);
          if (problem) return;
          mut.mutate();
        }}
        onCancel={() => {
          setOpen(false);
          mut.reset();
        }}
      />
    </>
  );
}
